/**
 * Attribute mapping utilities to handle different attribute key formats
 * between AI responses and Square attributes
 */

// Map AI response keys to Square attribute keys
export const AI_TO_SQUARE_KEY_MAP: Record<string, string> = {
  // Gender/Audience mappings
  'Gender': 'k.audience',
  'Wearer': 'k.audience',
  'Audience': 'k.audience',
  'k.Gender': 'k.audience',
  'Target Audience': 'k.audience',
  
  // Color mappings
  'Color': 'k.primary_color',
  'Primary_Color': 'k.primary_color',
  'Primary Color': 'k.primary_color',
  'Main_Color': 'k.primary_color',
  
  // Occasion/Formality mappings
  'Occasion': 'k.occasion',
  'Formality': 'k.occasion',
  'Occasion/Formality': 'k.occasion',
  
  // Season mappings
  'Season': 'k.season',
  'Seasonal': 'k.season',
  
  // Pattern mappings
  'Pattern': 'k.pattern',
  'Pattern_Type': 'k.pattern',
  'Design': 'k.pattern',
  
  // Material mappings
  'Material': 'k.material',
  'Fabric': 'k.material',
  'Material_Type': 'k.material',
  
  // Condition mappings
  'Condition': 'k.condition',
  'Quality': 'k.condition',
  
  // Motif mappings
  'Motifs': 'k.motifs',
  'Motif': 'k.motifs',
  'Design_Motifs': 'k.motifs',
  
  // Era mappings
  'Era': 'k.era',
  'Period': 'k.era',
  'Era/Period': 'k.era',

  // Size Category mappings
  'Size Category': 'k.size_category',
  'Size': 'k.size_category',

  // Pattern Classification mappings
  'Pattern Classification': 'k.pattern_classification',
  'Classification': 'k.pattern_classification',

  // Weave Type mappings
  'Weave Type': 'k.weave_type',
  'Weave': 'k.weave_type',

  // Craft Technique mappings
  'Craft Technique': 'k.craft_technique',
  'Technique': 'k.craft_technique',

  // Measurements mappings
  'Measurements': 'k.measurements',
};

// Map Square attribute keys back to AI keys (reverse mapping)
export const SQUARE_TO_AI_KEY_MAP: Record<string, string> = Object.entries(AI_TO_SQUARE_KEY_MAP)
  .reduce((acc, [aiKey, squareKey]) => {
    // Use the first AI key found for each Square key
    if (!acc[squareKey]) {
      acc[squareKey] = aiKey;
    }
    return acc;
  }, {} as Record<string, string>);

/**
 * Normalize an attribute key to match Square format
 * @param key The key from AI response
 * @returns The normalized Square key
 */
export function normalizeAttributeKey(key: string): string {
  // First check if it's already a Square key (starts with k.)
  if (key.startsWith('k.')) {
    return key;
  }
  
  // Check direct mapping
  if (AI_TO_SQUARE_KEY_MAP[key]) {
    return AI_TO_SQUARE_KEY_MAP[key];
  }
  
  // Try case-insensitive match
  const lowerKey = key.toLowerCase();
  for (const [aiKey, squareKey] of Object.entries(AI_TO_SQUARE_KEY_MAP)) {
    if (aiKey.toLowerCase() === lowerKey) {
      return squareKey;
    }
  }
  
  // If no mapping found, convert to k. format
  // Replace spaces and underscores with dots, lowercase
  const normalized = key.replace(/[\s_]+/g, '.').toLowerCase();
  return normalized.startsWith('k.') ? normalized : `k.${normalized}`;
}

/**
 * Map AI response attributes to Square attributes
 * @param aiAttributes Object with AI response attributes
 * @returns Object with Square-compatible attribute keys
 */
export function mapAIAttributesToSquare(aiAttributes: Record<string, any>): Record<string, any> {
  const mapped: Record<string, any> = {};
  
  for (const [key, value] of Object.entries(aiAttributes)) {
    const squareKey = normalizeAttributeKey(key);
    mapped[squareKey] = value;
  }
  
  return mapped;
}

/**
 * Extract and map custom attributes from AI response
 * @param aiResponse The full AI response object
 * @returns Mapped custom attributes ready for Square
 */
export function extractCustomAttributesFromAI(aiResponse: any): Record<string, any> {
  // Check for attributes in different possible locations
  const customAttrs = 
    aiResponse?.square_projection?.custom_attributes ||
    aiResponse?.custom_attributes ||
    aiResponse?.attributes ||
    {};
    
  return mapAIAttributesToSquare(customAttrs);
}

/**
 * Validate if an attribute value matches the expected type/format
 * @param value The attribute value
 * @param definition The attribute definition
 * @returns Boolean indicating if value is valid
 */
export function validateAttributeValue(value: any, definition: any): boolean {
  if (!definition) return true; // If no definition, accept any value
  
  // Check if value is in possible_values if defined
  if (definition.possible_values && definition.possible_values.length > 0) {
    if (Array.isArray(value)) {
      return value.every(v => definition.possible_values.includes(v));
    }
    return definition.possible_values.includes(value);
  }
  
  // Check type constraints
  switch (definition.type) {
    case 'STRING':
    case 'string':
    case 'text':
      return typeof value === 'string';
      
    case 'NUMBER':
    case 'number':
      return typeof value === 'number' || !isNaN(Number(value));
      
    case 'BOOLEAN':
    case 'boolean':
      return typeof value === 'boolean';
      
    case 'enum':
      return definition.possible_values?.includes(value) ?? true;
      
    case 'multi_select':
      return Array.isArray(value);
      
    default:
      return true;
  }
}